import React from "react";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";
import { EmojiEvents } from "@mui/icons-material";
import {
  getPriorityForType,
  getPriorityColor,
  getPriorityTier,
} from "../../../utils/calendarEventPriority";
import { PopulatedCalendarEvent } from "../../../api/calendarEvent";

type EventItemWithPriorityProps = {
  event: PopulatedCalendarEvent;
  isPrioritized: boolean;
  isMultiplePrioritized?: boolean;
  onClick?: () => void;
  variant?: "card" | "inline";
};

const EventItemWithPriority = ({
  event,
  isPrioritized,
  isMultiplePrioritized = false,
  onClick,
  variant = "card",
}: EventItemWithPriorityProps) => {
  const eventPriority = getPriorityForType(event.primaryType);

  if (variant === "inline") {
    return (
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        {isPrioritized && (
          <EmojiEvents sx={{ color: "#4caf50", fontSize: 20 }} />
        )}
        <DSChip
          label={event.primaryType}
          color={getPriorityColor(eventPriority)}
          size="small"
          variant="filled"
        />
        <DSChip
          label={`P${eventPriority} (${getPriorityTier(eventPriority)})`}
          size="small"
          variant="outlined"
        />
        {isPrioritized && (
          <DSChip
            label={isMultiplePrioritized ? "CO-PRIORITIZED" : "PRIORITIZED"}
            color="success"
            size="small"
            variant="filled"
          />
        )}
      </DSBox>
    );
  }

  return (
    <DSBox
      onClick={onClick}
      sx={{
        mb: 1,
        p: 1.5,
        border: isPrioritized ? "2px solid" : "1px solid",
        borderColor: isPrioritized ? "#66BB6A" : "divider",
        borderRadius: 1,
        backgroundColor: isPrioritized
          ? "rgba(102, 187, 106, 0.12)"
          : "white.5",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        "&:hover": onClick
          ? {
              backgroundColor: isPrioritized
                ? "rgba(102, 187, 106, 0.24)"
                : "white.10",
              transform: "translateX(4px)",
            }
          : {},
      }}
    >
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <DSBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          {isPrioritized && (
            <EmojiEvents
              sx={{
                color: "#4caf50",
                fontSize: 18,
              }}
            />
          )}
          <DSTypography
            variant="body2"
            fontWeight="bold"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.name}
          </DSTypography>
        </DSBox>
        <DSBox
          sx={{
            display: "flex",
            gap: 0.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <DSChip
            label={event.primaryType}
            color={getPriorityColor(eventPriority)}
            size="small"
            variant="filled"
          />
          <DSChip
            label={`P${eventPriority}`}
            size="small"
            variant="outlined"
            title={`Priority: ${eventPriority} (${getPriorityTier(eventPriority)})`}
          />
          {isPrioritized && (
            <DSChip
              label={isMultiplePrioritized ? "CO-PRIORITIZED" : "PRIORITIZED"}
              color="success"
              size="small"
              variant="filled"
            />
          )}
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default EventItemWithPriority;
